import { Card, Icon, IconButton, Tooltip } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import Footer from "examples/Footer";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "utils/Api";
import SmartTableCard from "utils/Smart Table/SmartTableCard";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SoftInput from "components/SoftInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { getSecureItem } from "utils/SecureStorage";
import jwtDecode from "jwt-decode";
import AdminPropertiesEditForm from "./AdminPropertiesEditForm";
import { useAuth } from "context/AuthContext";
import DeleteForm from "layouts/admin/Organization/DeleteForm";
import { useHierarchy } from "context/HierarchyContext";
import { showSuccess } from "utils/messageAlerts";
import { canCreate, canUpdate, canDelete } from "utils/permissionManager";
import { setSecureItem } from "utils/SecureStorage";
import { resetPermissionCache } from "utils/permissionManager";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ConfirmDialog from "utils/ConfirmDialog";

function AdminProperties() {
  const [allPropertiesData, setAllPropertiesData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupMode, setPopupMode] = useState("create"); // "create" or "edit"
  const navigate = useNavigate();
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const orgId = jwtDecode(getSecureItem("accessToken"))?.organization_id;
  const { isAdmin, isManager, isStaff, isUser, updateRole } = useAuth();
  const { setPropertyId, setOrgId } = useHierarchy();
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null,
    property: null,
  });

  // const handlePropertyClick = (property) => {
  //   updateRole(property.role_id); // ⭐ update role FIRST
  //   setSelectedProperty(property);
  //   setPropertyId(property.id);
  //   navigate(
  //     isAdmin
  //       ? `/admin/properties/${property.property_code}/unit`
  //       : isManager
  //       ? `/manager/properties/${property.property_code}/unit`
  //       : isStaff
  //       ? `/concierge/properties/${property.property_code}/unit`
  //       : isUser
  //       ? `/user/properties/${property.property_code}/unit`
  //       : `/unauthorized`
  //   );
  // };

  const fetchAndStoreAccessData = async () => {
    try {
      const [permissionsRes, entitiesRes] = await Promise.all([
        api.accessControl.getAccessControl({ organization_id: orgId }),
        api.accessControl.getEnumsEntity(),
      ]);

      setSecureItem("accessControl", permissionsRes);
      setSecureItem("entities", entitiesRes);
      resetPermissionCache();
    } catch (error) {}
  };

  // const handlePropertyClick = (property) => {
  //   const targetPath =
  //     property.role_id === 2
  //       ? `/admin/properties/${property.property_code}/unit`
  //       : property.role_id === 3
  //       ? `/manager/properties/${property.property_code}/unit`
  //       : property.role_id === 4
  //       ? `/concierge/${property.property_code}/unit`
  //       : property.role_id === 5
  //       ? `/user/properties/${property.property_code}/unit`
  //       : `/unauthorized`;

  //   updateRole(property.role_id); // change role AFTER calculating path
  //   setOrgId(property.organization_id);
  //   setSelectedProperty(property);
  //   setPropertyId(property.id);

  //   if ([3, 4, 5].includes(property.role_id)) {
  //     fetchAndStoreAccessData();
  //   }
  //   navigate(targetPath);
  // };

  const handlePropertyClick = async (property) => {
    const currentOrgId = getSecureItem("org_id");
    const currentRoleId = Number(getSecureItem("role_id"));

    const newRoleId = property.role_id;
    const newOrgId = property.organization_id;

    const roleChanged = currentRoleId !== newRoleId;
    const orgChanged = currentOrgId !== newOrgId;

    // If admin roles → remove permissions
    if ([1, 2].includes(newRoleId)) {
      setSecureItem("accessControl", []);
      setSecureItem("entities", []);
      resetPermissionCache();
    }

    // If role or org changed → reload permissions
    if ((roleChanged || orgChanged) && [3, 4, 5].includes(newRoleId)) {
      await fetchAndStoreAccessData(newOrgId);
    }

    // update storage
    setSecureItem("role_id", newRoleId);
    setSecureItem("orgId", newOrgId);

    updateRole(newRoleId);
    setOrgId(newOrgId);
    setSelectedProperty(property);
    setPropertyId(property.id);

    const targetPath =
      newRoleId === 2
        ? `/admin/properties/${property.property_code}/unit`
        : newRoleId === 3
        ? `/manager/properties/${property.property_code}/unit`
        : newRoleId === 4
        ? `/concierge/unit`
        : newRoleId === 5
        ? `/user/properties/${property.property_code}/unit`
        : `/unauthorized`;

    navigate(targetPath);
  };

  useEffect(() => {
    if (allPropertiesData.length > 0) {
      const firstProperty = allPropertiesData[0];

      updateRole(firstProperty.role_id);
      setPropertyId(firstProperty.id);
    }
  }, [allPropertiesData]);

  const canEdit = canUpdate("Property");
  const canRemove = canDelete("Property");
  const showActionsColumn = canEdit || canRemove;

  // Data fetching logic
  const fetchPropertiesData = async () => {
    try {
      const response = await api.multiOrgProperty.getMultiOrgProperties();

      setAllPropertiesData(response);
    } catch {
      setAllPropertiesData([]);
    }
  };

  useEffect(() => {
    fetchPropertiesData();
  }, []);

  useEffect(() => {
    setFilteredProperties(allPropertiesData);
  }, [allPropertiesData]);

  // Search Properties
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (!value.trim()) {
      setFilteredProperties(allPropertiesData);
      return;
    }
    const filtered = allPropertiesData.filter((property) =>
      property.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredProperties(filtered);
  };

  const handleEditProperties = (property) => {
    setPopupMode("edit");
    setSelectedProperty(property);
    setIsPopupOpen(true);
  };

  const handleDeleteProperty = async (id) => {
    if (!id) {
      console.error("Property ID is missing");
      return;
    }

    try {
      await api.properties.deleteProperty(id);

      showSuccess("Property soft deleted successfully");

      setDeletePopupOpen(false);
      setSelectedProperty(null);

      fetchPropertiesData();
    } catch (error) {}
  };

  const handlePropertyActionClick = (property, action) => {
    setConfirmDialog({
      open: true,
      type: action,
      property,
    });
  };

  const handleConfirmAction = async () => {
    const { property, type } = confirmDialog;

    try {
      if (type === "delete") {
        await api.properties.hardDeleteProperty(property.id);
        showSuccess("Property permanently deleted successfully");
      } else {
        await api.properties.restoreProperty({
          property_id: property.id,
          organization_id: orgId,
        });
        showSuccess("Property restored successfully");
      }

      fetchPropertiesData();
    } catch (e) {}

    setConfirmDialog({ open: false, type: null, property: null });
  };

  const content = (
    <Card>
      <SmartTableCard
        title={<h4>Properties in Organization</h4>}
        headerRight={[
          canCreate("Property") && (
            <SoftButton
              key="add-btn"
              variant="outlined"
              color="info"
              size="small"
              onClick={() => {
                setPopupMode("create");
                setSelectedProperty(null);
                setIsPopupOpen(true);
              }}
            >
              Add Property
            </SoftButton>
          ),

          <SoftInput
            key="search-input"
            placeholder="Search Properties"
            value={searchTerm}
            onChange={handleSearch}
            size="small"
          />,
        ]}
        columns={[
          { label: "Property", key: "Property" },
          { label: "Description", key: "Description", align: "center" },
          { label: "Property Code", key: "PropertyCode", align: "center" },
          { label: "PinCode", key: "PinCode", align: "center" },
          ...(isAdmin || isManager ? [{ label: "Active", key: "Active", align: "center" }] : []),
          ...(showActionsColumn ? [{ label: "Actions", key: "Actions", align: "center" }] : []),
        ]}
        // rows={filteredProperties.map((property) => ({
        //   Property: (
        //     <SoftBox display="flex" alignItems="center">
        //       <SoftBox>
        //         <SoftTypography
        //           variant="button"
        //           sx={{
        //             cursor: "pointer",
        //             fontWeight: 600,
        //           }}
        //           onClick={() => handlePropertyClick(property)}
        //         >
        //           {property.name}
        //         </SoftTypography>
        //       </SoftBox>
        //     </SoftBox>
        //   ),
        //   Description: property.description,
        //   PropertyCode: property.property_code,
        //   PinCode: property.pin_code,
        //   ...(showActionsColumn && {
        //     Actions: (
        //       <SoftBox display="flex" justifyContent="center" gap="8px">
        //         {canEdit && (
        //           <IconButton
        //             onClick={() => handleEditProperties(property)}
        //             sx={{
        //               border: "1px solid #D0D5DD",
        //               backgroundColor: "#F9FAFB",
        //               width: 34,
        //               height: 34,
        //               "& svg": { fontSize: 18 },
        //               color: "#475467",
        //               "&:hover": {
        //                 backgroundColor: "#E0F2FE",
        //                 borderColor: "#38BDF8",
        //                 color: "#0284C7",
        //               },
        //             }}
        //           >
        //             <EditIcon fontSize="sm" />
        //           </IconButton>
        //         )}

        //         {canRemove && (
        //           <IconButton
        //             onClick={() => {
        //               setDeletePopupOpen(true);
        //               setSelectedProperty(property);
        //             }}
        //             sx={{
        //               border: "1px solid #FECACA",
        //               backgroundColor: "#FEF2F2",
        //               width: 34,
        //               height: 34,
        //               "& svg": { fontSize: 18 },
        //               color: "#B91C1C",
        //               "&:hover": {
        //                 backgroundColor: "#FEE2E2",
        //                 borderColor: "#F87171",
        //                 color: "#991B1B",
        //               },
        //             }}
        //           >
        //             <DeleteIcon fontSize="sm" />
        //           </IconButton>
        //         )}
        //       </SoftBox>
        //     ),
        //   }),
        // }))}
        rows={filteredProperties.map((property) => {
          const isInactive = !property.active;
          const isRestrictedRole = [3, 4, 5].includes(property.role_id);
          const canManage = (isAdmin || isManager) && !isRestrictedRole;

          return {
            Property: (
              <SoftBox display="flex" alignItems="center">
                <SoftTypography
                  variant="button"
                  sx={{ cursor: "pointer", fontWeight: 600 }}
                  onClick={() => handlePropertyClick(property)}
                >
                  {property.name}
                </SoftTypography>
              </SoftBox>
            ),

            Description: property.description,
            PropertyCode: property.property_code,
            PinCode: property.pin_code,
            ...(isAdmin || isManager
              ? {
                  Active: property.active ? (
                    <SoftTypography variant="button" color="success" fontWeight="bold">
                      Yes
                    </SoftTypography>
                  ) : canManage ? (
                    <SoftBox display="flex" justifyContent="center" gap="6px">
                      {/* Restore */}
                      <Tooltip title="Restore Property" arrow>
                        <IconButton onClick={() => handlePropertyActionClick(property, "restore")} sx={{
                          width: 30,
                          height: 30,
                          color: "#16A34A",
                          backgroundColor: "#ECFDF5",
                          "&:hover": {
                            backgroundColor: "#D1FAE5",
                            transform: "scale(1.1)",
                          },
                        }}>
                          <RestoreIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>

                      {/* Delete */}
                      <Tooltip title="Delete Property Permanently" arrow>
                        <IconButton onClick={() => handlePropertyActionClick(property, "delete")} sx={{
                          width: 30,
                          height: 30,
                          color: "#DC2626",
                          backgroundColor: "#FEF2F2",
                          "&:hover": {
                            backgroundColor: "#FEE2E2",
                            transform: "scale(1.1)",
                          },
                        }}>
                          <DeleteForeverIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </SoftBox>
                  ) : (
                    <SoftTypography variant="button" color="error" fontWeight="bold">
                      No
                    </SoftTypography>
                  ),
                }
              : {}),
            ...(showActionsColumn && {
              Actions: (
                <SoftBox display="flex" justifyContent="center" gap="8px">
                  {canEdit && !isRestrictedRole && (
                    <IconButton
                      disabled={isInactive}
                      onClick={() => handleEditProperties(property)}
                      sx={{
                        border: "1px solid #D0D5DD",
                        backgroundColor: "#F9FAFB",
                        width: 34,
                        height: 34,
                        "& svg": { fontSize: 18 },
                        color: "#475467",
                        "&:hover": {
                          backgroundColor: "#E0F2FE",
                          borderColor: "#38BDF8",
                          color: "#0284C7",
                        },
                      }}
                    >
                      <EditIcon fontSize="sm" />
                    </IconButton>
                  )}

                  {canRemove && !isRestrictedRole && (
                    <IconButton
                      onClick={() => {
                        setDeletePopupOpen(true);
                        setSelectedProperty(property);
                      }}
                      disabled={isInactive}
                      sx={{
                        border: "1px solid #FECACA",
                        backgroundColor: "#FEF2F2",
                        width: 34,
                        height: 34,
                        "& svg": { fontSize: 18 },
                        color: "#B91C1C",
                        "&:hover": {
                          backgroundColor: "#FEE2E2",
                          borderColor: "#F87171",
                          color: "#991B1B",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="sm" />
                    </IconButton>
                  )}
                </SoftBox>
              ),
            }),
          };
        })}
      />
      {isPopupOpen && (
        <AdminPropertiesEditForm
          mode={popupMode}
          refreshProperties={fetchPropertiesData}
          organization_id={orgId}
          property={selectedProperty}
          onClose={() => {
            setIsPopupOpen(false);
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        type={confirmDialog.type}
        title={confirmDialog.type === "delete" ? "Delete Property" : "Restore Property"}
        description={
          confirmDialog.type === "delete"
            ? "This will permanently delete the property. This action cannot be undone."
            : "Are you sure you want to restore this property?"
        }
        confirmText={confirmDialog.type === "delete" ? "Delete Permanently" : "Restore"}
        onClose={() => setConfirmDialog({ open: false, type: null, property: null })}
        onConfirm={handleConfirmAction}
      />

      {deletePopupOpen && (
        <DeleteForm
          data={selectedProperty}
          type="Property"
          displayField="name"
          onClose={() => {
            setDeletePopupOpen(false);
            setSelectedProperty(null);
          }}
          onDelete={handleDeleteProperty}
        />
      )}
    </Card>
  );

  return (
    <>
      {isStaff || isUser ? (
        <DashboardLayout>
          <DashboardNavbar />
          {content}
          <Footer />
        </DashboardLayout>
      ) : (
        content
      )}
    </>
  );
}
export default AdminProperties;